/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { capitalize } from 'lodash';
import { Check, RotateCcw, X } from 'lucide-react';
import {
  CorrectReleaseRequestEditsInnerOpEnum,
  RecordingWestburyContributionsInner,
} from '../../../../../shared_components/generated/admin';
import type { Row } from '../MasterDiscographyTypes';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import WModal from 'shared_components/components/WModal';
import { useApi } from 'shared_components/context';

// Artist with optional featured flag (for create-new flow)
type ArtistEntry = { name: string; isFeatured: boolean };

// Confidence category styling (same as main table / ContributionPill)
const CONFIDENCE_CATEGORIES: {
  id: string;
  label: string;
  color: string;
  textColor: string;
}[] = [
  {
    id: '1',
    label: 'Verified (No Effort)',
    color: '#6EE038',
    textColor: 'black',
  },
  {
    id: '2',
    label: 'Inferred (Low Effort)',
    color: '#F6FF00',
    textColor: 'black',
  },
  {
    id: '3',
    label: 'Credited (Mid Effort)',
    color: '#FF8A00',
    textColor: 'white',
  },
  {
    id: '4',
    label: 'Unverified (High Effort)',
    color: '#FF2600',
    textColor: 'white',
  },
  {
    id: '5',
    label: 'Unknown (Max Effort)',
    color: '#AAB2B9',
    textColor: 'black',
  },
];

function getContributionPillStyle(categoryId: string): {
  backgroundColor: string;
  color: string;
} {
  const cat =
    CONFIDENCE_CATEGORIES.find((c) => c.id === categoryId) ??
    CONFIDENCE_CATEGORIES[4];
  return { backgroundColor: cat.color, color: cat.textColor };
}

function parseContributionEvidenceNotes(confidencePattern: string): {
  evidenceUrl: string;
  notes: string;
} {
  if (!confidencePattern?.trim()) return { evidenceUrl: '', notes: '' };
  try {
    const o = JSON.parse(confidencePattern) as unknown;
    if (typeof o === 'object' && o && !Array.isArray(o)) {
      const obj = o as Record<string, unknown>;
      return {
        evidenceUrl: typeof obj.evidenceUrl === 'string' ? obj.evidenceUrl : '',
        notes: typeof obj.notes === 'string' ? obj.notes : '',
      };
    }
  } catch {
    // ignore
  }
  return { evidenceUrl: '', notes: '' };
}

export interface RecordingEditFormProps {
  row: Row | null;
  onSave: (row: Row) => void;
  onCancel: () => void;
  /** When true, release and recording sections stack in one column (e.g. for side panel). */
  stacked?: boolean;
  /** When true, do not render the footer (e.g. when used in panel with external fixed footer). */
  hideFooter?: boolean;
  /** When true, show only the Release section (hide Recording & Artist section). */
  releaseOnly?: boolean;
  /** User ID (required for release lookup in Change Release modal). */
  userId?: string;
  availableFormats?: string[];
  availableTypes?: string[];
  availableContributions?: string[];
  availableArtists?: string[];
}

export interface RecordingEditFormHandle {
  save: () => void;
  cancel: () => void;
}

export const RecordingEditForm = forwardRef<
  RecordingEditFormHandle,
  RecordingEditFormProps
>(function RecordingEditForm(
  {
    row,
    onSave,
    onCancel,
    stacked = false,
    hideFooter = false,
    releaseOnly = false,
    userId,
    availableFormats = [],
    availableTypes = [],
    availableContributions = [],
    availableArtists = [],
  },
  ref
) {
  const { adminApi: api } = useApi();
  const [formData, setFormData] = useState<Partial<Row>>({});
  const [labels, setLabels] = useState<string[]>([]);
  const [catNumbers, setCatNumbers] = useState<string[]>([]);
  const [versions, setVersions] = useState<string[]>([]);
  const [isrcs, setIsrcs] = useState<string[]>([]);
  const [artists, setArtists] = useState<ArtistEntry[]>([]);
  const [contributions, setContributions] = useState<
    RecordingWestburyContributionsInner[]
  >([]);
  const [showReleaseCorrectionModal, setShowReleaseCorrectionModal] =
    useState(false);
  const [showChangeReleaseModal, setShowChangeReleaseModal] = useState(false);
  const [releaseId, setReleaseId] = useState('');
  const [releaseSource, setReleaseSource] = useState<
    'Discogs' | 'Deezer' | 'Spotify' | 'MusicBrainz' | 'PPL' | 'Other'
  >('Discogs');
  const [sourceUrl, setSourceUrl] = useState('');
  const [newReleaseTitle, setNewReleaseTitle] = useState('');
  const [newReleaseYear, setNewReleaseYear] = useState('');
  const [newReleaseLabels, setNewReleaseLabels] = useState<string[]>([]);
  const [newReleaseCatNumbers, setNewReleaseCatNumbers] = useState<string[]>(
    []
  );
  const [newReleaseFormat, setNewReleaseFormat] = useState('');
  const [newReleaseType, setNewReleaseType] = useState('');
  const [isReleaseEditable, setIsReleaseEditable] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [revertTarget, setRevertTarget] = useState<{
    type: 'section' | 'field';
    field?: string;
  } | null>(null);
  const [releaseLookupLoading, setReleaseLookupLoading] = useState(false);
  const [releaseLookupError, setReleaseLookupError] = useState<string | null>(
    null
  );
  const [releaseLookupSuccess, setReleaseLookupSuccess] = useState(false);
  const [releaseLookupNotFound, setReleaseLookupNotFound] = useState(false);
  const [releaseLookupNotLinked, setReleaseLookupNotLinked] = useState(false);
  const [releaseNotLinkedConfirmed, setReleaseNotLinkedConfirmed] =
    useState(false);
  const [showLiveWarningModal, setShowLiveWarningModal] = useState(false);
  const [pendingLiveValue, setPendingLiveValue] = useState<boolean | null>(
    null
  );
  const [showCreateArtist, setShowCreateArtist] = useState(false);
  const [newArtistAlias, setNewArtistAlias] = useState('');
  const [newArtistFeatured, setNewArtistFeatured] = useState(false);
  const [artistSearchQuery, setArtistSearchQuery] = useState('');
  const [artistDropdownOpen, setArtistDropdownOpen] = useState(false);
  const [artistsError, setArtistsError] = useState<string | null>(null);
  const [contributionCategoryOpenIndex, setContributionCategoryOpenIndex] =
    useState<number | null>(null);
  const [categoryDropdownRect, setCategoryDropdownRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  const [showContributionModal, setShowContributionModal] = useState(false);
  const [contributionModalContext, setContributionModalContext] = useState<
    | { type: 'edit'; index: number }
    | { type: 'add'; contributionValue: string }
    | null
  >(null);
  const [contributionModalForm, setContributionModalForm] = useState<{
    contributionName: string;
    confidenceCategory: string;
    evidenceUrl: string;
    notes: string;
  }>({
    contributionName: '',
    confidenceCategory: '5',
    evidenceUrl: '',
    notes: '',
  });
  const [contributionModalEvidenceError, setContributionModalEvidenceError] =
    useState<string | null>(null);
  const [releaseSaveLoading, setReleaseSaveLoading] = useState(false);
  const [originalValues, setOriginalValues] = useState<{
    formData: Partial<Row>;
    labels: string[];
    catNumbers: string[];
    versions: string[];
    isrcs: string[];
    artists: ArtistEntry[];
    contributions: RecordingWestburyContributionsInner[];
  } | null>(null);

  useEffect(() => {
    const validCount = artists.filter((a) => a.name.trim()).length;
    if (validCount > 0 && artistsError) setArtistsError(null);
  }, [artists, artistsError]);

  useEffect(() => {
    if (row) {
      setArtistsError(null);
      const initialFormData = {
        release: row.release,
        year: row.year,
        format: row.format,
        mediaType: row.mediaType,
        title: row.title,
        duration: row.duration,
        isLive: row.isLive,
        isFeatured: row.isFeatured,
      };
      setFormData(initialFormData);

      // Parse comma-separated values into arrays
      const initialLabels = row.label
        ? row.label
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v)
        : [];
      const initialCatNumbers = row.catNumbers
        ? row.catNumbers
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v)
        : [];
      const initialVersions = row.version
        ? row.version
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v)
        : [];
      const initialIsrcs = row.isrcs
        ? row.isrcs
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v)
        : [];
      const initialArtists: ArtistEntry[] = row.artist
        ? row.artist
            .split(',')
            .map((name) => ({ name: name.trim(), isFeatured: false }))
            .filter((a) => a.name)
        : [];
      const initialContributions: RecordingWestburyContributionsInner[] =
        row.contributions && row.contributions.length > 0
          ? row.contributions.map((c) => ({
              contribution: c.contribution,
              confidenceCategory: c.confidenceCategory ?? '5',
              confidencePattern: c.confidencePattern ?? '',
            }))
          : [];

      setLabels(initialLabels);
      setCatNumbers(initialCatNumbers);
      setVersions(initialVersions);
      setIsrcs(initialIsrcs);
      setArtists(initialArtists);
      setContributions(initialContributions);
      setShowCreateArtist(false);
      setNewArtistAlias('');
      setNewArtistFeatured(false);
      setArtistSearchQuery('');
      setArtistDropdownOpen(false);
      setContributionCategoryOpenIndex(null);
      setCategoryDropdownRect(null);
      setShowContributionModal(false);
      setContributionModalContext(null);

      // Store original values for revert functionality
      setOriginalValues({
        formData: initialFormData,
        labels: initialLabels,
        catNumbers: initialCatNumbers,
        versions: initialVersions,
        isrcs: initialIsrcs,
        artists: initialArtists,
        contributions: initialContributions,
      });

      // Reset release editing state when row changes
      setIsReleaseEditable(false);
    }
  }, [row]);

  const saveHandlerRef = useRef<() => void>(() => {});
  useImperativeHandle(
    ref,
    () => ({
      save: () => saveHandlerRef.current?.(),
      cancel: onCancel,
    }),
    [onCancel]
  );

  if (!row || !originalValues) return null;

  // Helper function to check if a field has been modified
  const isFieldModified = (field: string): boolean => {
    if (!originalValues) return false;

    switch (field) {
      case 'release':
        return formData.release !== originalValues.formData.release;
      case 'year':
        return formData.year !== originalValues.formData.year;
      case 'label':
        return JSON.stringify(labels) !== JSON.stringify(originalValues.labels);
      case 'catNumbers':
        return (
          JSON.stringify(catNumbers) !==
          JSON.stringify(originalValues.catNumbers)
        );
      case 'format':
        return formData.format !== originalValues.formData.format;
      case 'mediaType':
        return formData.mediaType !== originalValues.formData.mediaType;
      case 'artist':
        return (
          JSON.stringify(artists) !== JSON.stringify(originalValues.artists)
        );
      case 'isFeatured':
        return false; // Featured is read-only, never considered modified
      case 'title':
        return formData.title !== originalValues.formData.title;
      case 'version':
        return (
          JSON.stringify(versions) !== JSON.stringify(originalValues.versions)
        );
      case 'isrcs':
        return JSON.stringify(isrcs) !== JSON.stringify(originalValues.isrcs);
      case 'duration':
        return formData.duration !== originalValues.formData.duration;
      case 'isLive':
        return formData.isLive !== originalValues.formData.isLive;
      case 'contributions':
        return (
          JSON.stringify(contributions) !==
          JSON.stringify(originalValues.contributions)
        );
      default:
        return false;
    }
  };

  // Check if any field in release section has been modified
  const isReleaseSectionModified = (): boolean => {
    return (
      isFieldModified('release') ||
      isFieldModified('year') ||
      isFieldModified('label') ||
      isFieldModified('catNumbers') ||
      isFieldModified('format') ||
      isFieldModified('mediaType')
    );
  };

  // Check if any field in recording section has been modified
  const isRecordingSectionModified = (): boolean => {
    return (
      isFieldModified('artist') ||
      isFieldModified('title') ||
      isFieldModified('version') ||
      isFieldModified('isrcs') ||
      isFieldModified('duration') ||
      isFieldModified('isLive') ||
      isFieldModified('isFeatured') ||
      isFieldModified('contributions')
    );
  };

  const handleReleaseLookup = async () => {
    if (!userId || !row?.id || !releaseId.trim()) {
      setReleaseLookupError(
        'User and recording context required. Enter a release ID.'
      );
      return;
    }
    setReleaseLookupError(null);
    setReleaseLookupSuccess(false);
    setReleaseLookupNotFound(false);
    setReleaseLookupNotLinked(false);
    setReleaseNotLinkedConfirmed(false);
    setReleaseLookupLoading(true);
    // Mock: source ID 99 simulates "no result" – user can fill details manually
    if (releaseId.trim() === '99') {
      setReleaseLookupNotFound(true);
      setReleaseLookupLoading(false);
      return;
    }
    try {
      let response = await api.lookupLookupReleaseResponse({
        userId,
        parentRecordingId: row.id,
        sourceId: releaseId.trim(),
        source: 'discogs',
      });
      // Mock: source ID 88 uses real API but response is forced to linked: false
      if (releaseId.trim() === '88') {
        response = { ...response, linked: false };
      }
      const p = response.payload;
      if (p.releaseTitle) setNewReleaseTitle(p.releaseTitle);
      if (p.dateReleased) {
        const year =
          p.dateReleased.length >= 4
            ? p.dateReleased.slice(0, 4)
            : p.dateReleased;
        setNewReleaseYear(year);
      }
      if (p.releaseLabels?.length) setNewReleaseLabels([...p.releaseLabels]);
      if (p.catNumbers?.length) setNewReleaseCatNumbers([...p.catNumbers]);
      if (p.format) setNewReleaseFormat(p.format);
      if (p.mediaType) setNewReleaseType(p.mediaType);
      setReleaseLookupSuccess(true);
      if (!response.linked) {
        setReleaseLookupNotLinked(true);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Release lookup failed';
      setReleaseLookupError(message);
    } finally {
      setReleaseLookupLoading(false);
    }
  };

  const handleRevert = (type: 'section' | 'field', field?: string) => {
    if (type === 'section') {
      // Revert entire section
      if (field === 'release') {
        setFormData((prev) => ({
          ...prev,
          release: originalValues.formData.release,
          year: originalValues.formData.year,
          format: originalValues.formData.format,
          mediaType: originalValues.formData.mediaType,
        }));
        setLabels(originalValues.labels);
        setCatNumbers(originalValues.catNumbers);
      } else if (field === 'recording') {
        setFormData((prev) => ({
          ...prev,
          title: originalValues.formData.title,
          duration: originalValues.formData.duration,
          isLive: originalValues.formData.isLive,
          isFeatured: originalValues.formData.isFeatured,
        }));
        setVersions(originalValues.versions);
        setIsrcs(originalValues.isrcs);
        setArtists(originalValues.artists);
        setContributions(originalValues.contributions);
        setContributionCategoryOpenIndex(null);
        setCategoryDropdownRect(null);
      }
    } else if (type === 'field' && field) {
      // Revert specific field
      switch (field) {
        case 'release':
          handleFieldChange('release', originalValues.formData.release || '');
          break;
        case 'year':
          handleFieldChange('year', originalValues.formData.year || '');
          break;
        case 'label':
          setLabels(originalValues.labels);
          break;
        case 'catNumbers':
          setCatNumbers(originalValues.catNumbers);
          break;
        case 'format':
          handleFieldChange('format', originalValues.formData.format || '');
          break;
        case 'mediaType':
          handleFieldChange(
            'mediaType',
            originalValues.formData.mediaType || ''
          );
          break;
        case 'artist':
          setArtists(originalValues.artists);
          break;
        case 'title':
          handleFieldChange('title', originalValues.formData.title || '');
          break;
        case 'version':
          setVersions(originalValues.versions);
          break;
        case 'isrcs':
          setIsrcs(originalValues.isrcs);
          break;
        case 'duration':
          handleFieldChange('duration', originalValues.formData.duration || '');
          break;
        case 'isLive':
          handleFieldChange('isLive', originalValues.formData.isLive || false);
          break;
        case 'isFeatured':
          handleFieldChange(
            'isFeatured',
            originalValues.formData.isFeatured || false
          );
          break;
        case 'contributions':
          setContributions(originalValues.contributions);
          setContributionCategoryOpenIndex(null);
          setCategoryDropdownRect(null);
          break;
      }
    }
    setShowRevertModal(false);
    setRevertTarget(null);
  };

  const handleReleaseCancel = () => {
    handleRevert('section', 'release');
    onCancel();
  };

  const handleSaveReleaseDetails = async () => {
    if (!userId || !row?.id || !row.earliestParentReleaseId) return;
    setReleaseSaveLoading(true);
    try {
      const edits: Array<{
        op: CorrectReleaseRequestEditsInnerOpEnum;
        field: string;
        value: string;
      }> = [];
      if (isFieldModified('release')) {
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'release_title',
          value: formData.release ?? '',
        });
      }
      if (isFieldModified('year')) {
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'date_released',
          value: formData.year ?? '',
        });
      }
      if (isFieldModified('label')) {
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'release_labels',
          value: labels.join(', '),
        });
      }
      if (isFieldModified('catNumbers')) {
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'cat_numbers',
          value: catNumbers.join(', '),
        });
      }
      if (isFieldModified('format')) {
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'format',
          value: formData.format ?? '',
        });
      }
      if (isFieldModified('mediaType')) {
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'media_type',
          value: formData.mediaType ?? '',
        });
      }
      if (edits.length > 0) {
        await api.correctServiceResponse({
          userId,
          parentRecordingId: row.id,
          correctReleaseRequest: {
            releaseId: row.earliestParentReleaseId,
            edits,
          },
        });
      }
    } finally {
      setIsReleaseEditable(false);
      setReleaseSaveLoading(false);
    }
  };

  const handleFieldChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Helper functions for managing multiple values
  const addMultipleValue = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    value: string
  ) => {
    if (value.trim()) {
      setter((prev) => [...prev, value.trim()]);
    }
  };

  const removeMultipleValue = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    index: number
  ) => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  const updateMultipleValue = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    index: number,
    value: string
  ) => {
    setter((prev) => prev.map((v, i) => (i === index ? value.trim() : v)));
  };

  const handleSave = () => {
    // Ensure at least one artist (per spec requirement)
    const validArtists = artists.filter((a) => a.name.trim());
    if (validArtists.length === 0) {
      setArtistsError('At least one artist is required');
      return;
    }
    setArtistsError(null);

    // Ensure at least one contribution (per spec requirement)
    const validContributions = contributions.filter((c) =>
      (c.contribution ?? '').trim()
    );
    if (validContributions.length === 0) {
      // Can't save without at least one contribution
      alert('At least one contribution is required');
      return;
    }

    const updatedRow: Row = {
      ...row,
      ...formData,
      label: labels.join(', '),
      catNumbers: catNumbers.join(', '),
      version: versions.join(', '),
      isrcs: isrcs.join(', '),
      artist: validArtists.map((a) => a.name).join(', '),
      contributions: validContributions.map((c) => ({
        contribution: c.contribution.trim(),
        confidenceCategory: c.confidenceCategory ?? '5',
        confidencePattern: c.confidencePattern ?? '',
      })),
      isModified: true,
    };
    onSave(updatedRow);
  };

  saveHandlerRef.current = handleSave;

  return (
    <>
      <div
        className={
          stacked
            ? 'tw-flex tw-flex-col tw-gap-0 tw-mt-2 tw-pb-2'
            : 'tw-grid tw-grid-cols-[1fr_auto_1fr] tw-gap-0 tw-mt-2 tw-pb-2 tw-overflow-y-auto tw-max-h-[calc(90vh-100px)]'
        }
      >
        {/* Release Section */}
        <div
          className={
            stacked
              ? 'tw-p-3 tw-flex tw-flex-col tw-shrink-0'
              : 'tw-pr-4 tw-p-3 tw-flex tw-flex-col tw-overflow-y-auto'
          }
        >
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-2 tw-pb-1.5 tw-border-b tw-border-gray-600 tw-flex-shrink-0">
            <div className="tw-text-white tw-font-semibold">Release</div>
            {isReleaseSectionModified() && (
              <button
                type="button"
                onClick={() => {
                  setRevertTarget({ type: 'section', field: 'release' });
                  setShowRevertModal(true);
                }}
                className="tw-text-gray-400 hover:tw-text-white tw-flex tw-items-center tw-gap-1 tw-text-xs"
                title="Revert all release fields"
              >
                <RotateCcw size={14} />
                <span>Revert</span>
              </button>
            )}
          </div>
          <div className="tw-space-y-4 tw-flex-1">
            {/* Release Title and Year of Release - same row */}
            <div className="tw-flex tw-gap-4 tw-w-full">
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                  <label
                    htmlFor="modal-release-title"
                    className={`tw-block tw-text-sm tw-font-medium ${
                      isFieldModified('release')
                        ? 'tw-text-orange-400'
                        : 'tw-text-white'
                    }`}
                  >
                    Release Title
                  </label>
                </div>
                <input
                  id="modal-release-title"
                  type="text"
                  value={formData.release || ''}
                  onChange={(e) => handleFieldChange('release', e.target.value)}
                  readOnly={!isReleaseEditable}
                  className={`tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue ${
                    !isReleaseEditable
                      ? 'tw-cursor-not-allowed tw-opacity-60'
                      : ''
                  }`}
                />
              </div>
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                  <label
                    htmlFor="modal-year"
                    className={`tw-block tw-text-sm tw-font-medium ${
                      isFieldModified('year')
                        ? 'tw-text-orange-400'
                        : 'tw-text-white'
                    }`}
                  >
                    Year of Release
                  </label>
                </div>
                <input
                  id="modal-year"
                  type="text"
                  value={
                    formData.year
                      ? new Date(formData.year).getFullYear().toString()
                      : ''
                  }
                  onChange={(e) => {
                    const yearValue = e.target.value;
                    if (yearValue && /^\d{4}$/.test(yearValue)) {
                      const date = new Date(`${yearValue}-01-01`);
                      handleFieldChange('year', date.toISOString());
                    } else if (yearValue === '') {
                      handleFieldChange('year', '');
                    }
                  }}
                  readOnly={!isReleaseEditable}
                  className={`tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue ${
                    !isReleaseEditable
                      ? 'tw-cursor-not-allowed tw-opacity-60'
                      : ''
                  }`}
                  placeholder="YYYY"
                  maxLength={4}
                />
              </div>
            </div>

            {/* Label (Multiple) */}
            <div>
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                <label
                  htmlFor="modal-label-input"
                  className={`tw-block tw-text-sm tw-font-medium ${
                    isFieldModified('label')
                      ? 'tw-text-orange-400'
                      : 'tw-text-white'
                  }`}
                >
                  Label (Multiple)
                </label>
                <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                  Optional
                </span>
              </div>
              {labels.length > 0 && (
                <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2 tw-min-h-[40px]">
                  {labels.map((label, index) => (
                    <div
                      key={index}
                      className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white ${
                        !isReleaseEditable ? 'tw-opacity-60' : ''
                      }`}
                    >
                      <span>{label}</span>
                      {isReleaseEditable && (
                        <button
                          type="button"
                          onClick={() => removeMultipleValue(setLabels, index)}
                          className="tw-ml-1 tw-text-gray-400 hover:tw-text-white tw-flex tw-items-center"
                          title="Remove"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                <input
                  id="modal-label-input"
                  type="text"
                  placeholder="Enter label"
                  readOnly={!isReleaseEditable}
                  disabled={!isReleaseEditable}
                  onKeyDown={(e) => {
                    if (
                      isReleaseEditable &&
                      e.key === 'Enter' &&
                      e.currentTarget.value.trim()
                    ) {
                      addMultipleValue(setLabels, e.currentTarget.value);
                      e.currentTarget.value = '';
                    }
                  }}
                  className={`tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue ${
                    !isReleaseEditable
                      ? 'tw-cursor-not-allowed tw-opacity-60'
                      : ''
                  }`}
                />
                {isReleaseEditable && (
                  <div className="tw-flex tw-justify-end">
                    <button
                      type="button"
                      className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                      onClick={() => {
                        const input = document.getElementById(
                          'modal-label-input'
                        ) as HTMLInputElement;
                        if (input && input.value.trim()) {
                          addMultipleValue(setLabels, input.value);
                          input.value = '';
                        }
                      }}
                    >
                      <i className="wi wi-20px wi-plus tw-mr-1" />
                      <span className="tw-underline">Add label</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Cat no. (Multiple) */}
            <div>
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                <label
                  htmlFor="modal-catno-input"
                  className={`tw-block tw-text-sm tw-font-medium ${
                    isFieldModified('catNumbers')
                      ? 'tw-text-orange-400'
                      : 'tw-text-white'
                  }`}
                >
                  Catalogue Number (Multiple)
                </label>
                <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                  Optional
                </span>
              </div>
              {catNumbers.length > 0 && (
                <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2 tw-min-h-[40px]">
                  {catNumbers.map((catNo, index) => (
                    <div
                      key={index}
                      className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white ${
                        !isReleaseEditable ? 'tw-opacity-60' : ''
                      }`}
                    >
                      <span>{catNo}</span>
                      {isReleaseEditable && (
                        <button
                          type="button"
                          onClick={() =>
                            removeMultipleValue(setCatNumbers, index)
                          }
                          className="tw-ml-1 tw-text-gray-400 hover:tw-text-white tw-flex tw-items-center"
                          title="Remove"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                <input
                  id="modal-catno-input"
                  type="text"
                  placeholder="Enter catalogue number"
                  readOnly={!isReleaseEditable}
                  disabled={!isReleaseEditable}
                  onKeyDown={(e) => {
                    if (
                      isReleaseEditable &&
                      e.key === 'Enter' &&
                      e.currentTarget.value.trim()
                    ) {
                      addMultipleValue(setCatNumbers, e.currentTarget.value);
                      e.currentTarget.value = '';
                    }
                  }}
                  className={`tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue ${
                    !isReleaseEditable
                      ? 'tw-cursor-not-allowed tw-opacity-60'
                      : ''
                  }`}
                />
                {isReleaseEditable && (
                  <div className="tw-flex tw-justify-end">
                    <button
                      type="button"
                      className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                      onClick={() => {
                        const input = document.getElementById(
                          'modal-catno-input'
                        ) as HTMLInputElement;
                        if (input && input.value.trim()) {
                          addMultipleValue(setCatNumbers, input.value);
                          input.value = '';
                        }
                      }}
                    >
                      <i className="wi wi-20px wi-plus tw-mr-1" />
                      <span className="tw-underline">Add catalogue number</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Format and Type - same row */}
            <div className="tw-flex tw-gap-4 tw-w-full">
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                  <label
                    htmlFor="modal-format"
                    className={`tw-block tw-text-sm tw-font-medium ${
                      isFieldModified('format')
                        ? 'tw-text-orange-400'
                        : 'tw-text-white'
                    }`}
                  >
                    Format
                  </label>
                </div>
                <select
                  id="modal-format"
                  value={formData.format || ''}
                  onChange={(e) => handleFieldChange('format', e.target.value)}
                  disabled={!isReleaseEditable}
                  className={`tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue ${
                    !isReleaseEditable
                      ? 'tw-cursor-not-allowed tw-opacity-60'
                      : ''
                  }`}
                >
                  <option value="">Select Format</option>
                  {availableFormats.map((format) => (
                    <option key={format} value={format}>
                      {format}
                    </option>
                  ))}
                </select>
              </div>
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                  <label
                    htmlFor="modal-type"
                    className={`tw-block tw-text-sm tw-font-medium ${
                      isFieldModified('mediaType')
                        ? 'tw-text-orange-400'
                        : 'tw-text-white'
                    }`}
                  >
                    Type
                  </label>
                </div>
                <select
                  id="modal-type"
                  value={formData.mediaType || ''}
                  onChange={(e) =>
                    handleFieldChange('mediaType', e.target.value)
                  }
                  disabled={!isReleaseEditable}
                  className={`tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue ${
                    !isReleaseEditable
                      ? 'tw-cursor-not-allowed tw-opacity-60'
                      : ''
                  }`}
                >
                  <option value="">Select Type</option>
                  {availableTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Correct Release Button */}
            <div className="tw-flex tw-gap-2 tw-items-center tw-mt-3 tw-pt-4 tw-border-t tw-border-gray-600">
              {!isReleaseEditable && (
                <WButton
                  variant="secondary"
                  label="Edit Release Details"
                  onClick={() => setShowReleaseCorrectionModal(true)}
                />
              )}
              {!(releaseOnly && isReleaseEditable) && (
                <WButton
                  variant="secondary"
                  label="Change Linked Release"
                  onClick={() => setShowChangeReleaseModal(true)}
                />
              )}
              {hideFooter && releaseOnly && isReleaseEditable && (
                <>
                  <WButton
                    variant="secondary"
                    label="Cancel"
                    onClick={handleReleaseCancel}
                  />
                  <WButton
                    variant="primary"
                    label={
                      releaseSaveLoading ? 'Saving…' : 'Save Release Details'
                    }
                    onClick={handleSaveReleaseDetails}
                    disabled={releaseSaveLoading}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {!releaseOnly && (
          <>
            {/* Separator (vertical in grid, horizontal in stacked) */}
            <div
              className={
                stacked
                  ? 'tw-border-t tw-border-gray-600 tw-shrink-0'
                  : 'tw-border-l tw-border-gray-600'
              }
            />

            {/* Recording Section */}
            <div
              className={
                stacked
                  ? 'tw-p-3 tw-flex tw-flex-col tw-shrink-0'
                  : 'tw-pl-4 tw-p-3 tw-flex tw-flex-col tw-overflow-y-auto'
              }
            >
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-2 tw-pb-1.5 tw-border-b tw-border-gray-600 tw-flex-shrink-0">
                <h3 className="tw-text-white tw-text-base tw-font-semibold">
                  Recording & Artist
                </h3>
                {isRecordingSectionModified() && (
                  <button
                    type="button"
                    onClick={() => {
                      setRevertTarget({ type: 'section', field: 'recording' });
                      setShowRevertModal(true);
                    }}
                    className="tw-text-gray-400 hover:tw-text-white tw-flex tw-items-center tw-gap-1 tw-text-xs"
                    title="Revert all recording fields"
                  >
                    <RotateCcw size={14} />
                    <span>Revert</span>
                  </button>
                )}
              </div>
              <div className="tw-space-y-2.5 tw-flex-1">
                {/* Artist (Multiple) — select from list, remove, or create new (alias + featured) */}
                <div>
                  <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                    <label
                      htmlFor="modal-artist-select"
                      className={`tw-text-xs tw-font-medium ${
                        isFieldModified('artist')
                          ? 'tw-text-orange-400'
                          : 'tw-text-white'
                      }`}
                    >
                      Artist (Multiple) *
                    </label>
                  </div>
                  {artistsError && (
                    <p className="tw-text-westburyRed tw-text-xs tw-mb-1.5">
                      {artistsError}
                    </p>
                  )}

                  <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2 tw-items-start">
                    <div className="tw-relative tw-flex-1 tw-min-w-[160px]">
                      <input
                        id="modal-artist-select"
                        type="text"
                        value={artistSearchQuery}
                        onChange={(e) => {
                          setArtistSearchQuery(e.target.value);
                          setArtistDropdownOpen(true);
                        }}
                        onFocus={() => setArtistDropdownOpen(true)}
                        onBlur={() => {
                          setTimeout(() => setArtistDropdownOpen(false), 200);
                        }}
                        placeholder="Search or select artist…"
                        className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                      />
                      {artistDropdownOpen && (
                        <div className="tw-absolute tw-z-50 tw-left-0 tw-right-0 tw-top-full tw-mt-0.5 tw-max-h-48 tw-overflow-y-auto tw-rounded tw-border tw-border-gray-600 tw-bg-gray-800 tw-py-1 tw-shadow-lg">
                          {(() => {
                            const notAdded = availableArtists.filter(
                              (a) => !artists.some((e) => e.name === a)
                            );
                            const q = artistSearchQuery.trim().toLowerCase();
                            const filtered = q
                              ? notAdded.filter((a) =>
                                  a.toLowerCase().includes(q)
                                )
                              : notAdded;
                            if (filtered.length === 0) {
                              return (
                                <div className="tw-px-3 tw-py-2 tw-text-gray-400 tw-text-xs">
                                  {notAdded.length === 0
                                    ? 'All artists added'
                                    : 'No matching artists'}
                                </div>
                              );
                            }
                            return filtered.map((name) => (
                              <button
                                key={name}
                                type="button"
                                className="tw-w-full tw-text-left tw-px-3 tw-py-1.5 tw-text-sm tw-text-white hover:tw-bg-gray-700 tw-transition-colors"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setArtists((prev) => [
                                    ...prev,
                                    { name, isFeatured: false },
                                  ]);
                                  setArtistSearchQuery('');
                                  setArtistDropdownOpen(false);
                                }}
                              >
                                {name}
                              </button>
                            ));
                          })()}
                        </div>
                      )}
                    </div>
                    <WButton
                      variant="secondary"
                      label="Create new artist"
                      onClick={() => setShowCreateArtist(true)}
                    />
                  </div>
                  <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mt-2">
                    {artists.map((artist, index) => (
                      <div
                        key={index}
                        className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white"
                      >
                        <span>
                          {artist.name}
                          {artist.isFeatured ? ' (Featured)' : ''}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setArtists((prev) =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="tw-ml-1 tw-text-gray-400 hover:tw-text-white tw-flex tw-items-center"
                          title="Remove"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recording Title */}
                <div>
                  <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                    <label
                      htmlFor="modal-title"
                      className={`tw-text-xs tw-font-medium ${
                        isFieldModified('title')
                          ? 'tw-text-orange-400'
                          : 'tw-text-white'
                      }`}
                    >
                      Recording Title *
                    </label>
                  </div>
                  <input
                    id="modal-title"
                    type="text"
                    value={formData.title || ''}
                    onChange={(e) => handleFieldChange('title', e.target.value)}
                    className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                  />
                </div>

                {/* Version (Multiple) — values editable only; cannot add or remove */}
                <div>
                  <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                    <label
                      htmlFor="modal-version-0"
                      className={`tw-text-xs tw-font-medium ${
                        isFieldModified('version')
                          ? 'tw-text-orange-400'
                          : 'tw-text-white'
                      }`}
                    >
                      Version (Multiple)
                    </label>
                  </div>
                  <div className="tw-flex tw-flex-col tw-gap-1.5">
                    {versions.map((version, index) => (
                      <input
                        key={index}
                        id={index === 0 ? 'modal-version-0' : undefined}
                        type="text"
                        value={version}
                        onChange={(e) =>
                          updateMultipleValue(
                            setVersions,
                            index,
                            e.target.value
                          )
                        }
                        className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                        placeholder="Version"
                      />
                    ))}
                    {versions.length === 0 && (
                      <span className="tw-text-gray-500 tw-text-xs">
                        No versions (cannot add new).
                      </span>
                    )}
                  </div>
                </div>

                {/* ISRCs (Multiple) — add or remove only; values cannot be edited */}
                <div>
                  <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                    <label
                      htmlFor="modal-isrcs-input"
                      className={`tw-text-xs tw-font-medium ${
                        isFieldModified('isrcs')
                          ? 'tw-text-orange-400'
                          : 'tw-text-white'
                      }`}
                    >
                      ISRCs (Multiple)
                    </label>
                  </div>

                  <div className="tw-flex tw-gap-1.5">
                    <input
                      id="modal-isrcs-input"
                      type="text"
                      placeholder="Enter ISRC"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                          addMultipleValue(setIsrcs, e.currentTarget.value);
                          e.currentTarget.value = '';
                        }
                      }}
                      className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                    />
                    <WButton
                      variant="secondary"
                      label="Add"
                      onClick={() => {
                        const input = document.getElementById(
                          'modal-isrcs-input'
                        ) as HTMLInputElement;
                        if (input && input.value.trim()) {
                          addMultipleValue(setIsrcs, input.value);
                          input.value = '';
                        }
                      }}
                    />
                  </div>
                  <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mt-2">
                    {isrcs.map((isrc, index) => (
                      <div
                        key={index}
                        className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white"
                      >
                        <span>{isrc}</span>
                        <button
                          type="button"
                          onClick={() => removeMultipleValue(setIsrcs, index)}
                          className="tw-ml-1 tw-text-gray-400 hover:tw-text-white tw-flex tw-items-center"
                          title="Remove"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Duration, Live, Featured — same row */}
                <div className="tw-flex tw-gap-4 tw-w-full">
                  {/* Duration — editable only when currently null; read-only once set */}
                  <div className="tw-flex-1 tw-min-w-0">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                      <label
                        htmlFor="modal-duration"
                        className={`tw-text-xs tw-font-medium ${
                          isFieldModified('duration')
                            ? 'tw-text-orange-400'
                            : originalValues?.formData?.duration
                            ? 'tw-text-gray-400'
                            : 'tw-text-white'
                        }`}
                      >
                        Duration
                      </label>
                    </div>
                    <input
                      id="modal-duration"
                      type="text"
                      value={formData.duration || ''}
                      onChange={(e) =>
                        handleFieldChange('duration', e.target.value)
                      }
                      disabled={
                        !!originalValues?.formData?.duration?.toString().trim()
                      }
                      className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-70 disabled:tw-bg-gray-800"
                      placeholder="HH:MM:SS"
                    />
                  </div>

                  {/* Live (Y/N Dropdown) */}
                  <div className="tw-flex-1 tw-min-w-0">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                      <label
                        htmlFor="modal-live"
                        className={`tw-text-xs tw-font-medium ${
                          isFieldModified('isLive')
                            ? 'tw-text-orange-400'
                            : 'tw-text-white'
                        }`}
                      >
                        Live
                      </label>
                    </div>
                    <select
                      id="modal-live"
                      value={formData.isLive ? 'Yes' : 'No'}
                      onChange={(e) => {
                        const newLive = e.target.value === 'Yes';
                        if (formData.isLive && !newLive) {
                          setPendingLiveValue(false);
                          setShowLiveWarningModal(true);
                        } else {
                          handleFieldChange('isLive', newLive);
                        }
                      }}
                      className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>

                  {/* Featured (F/NF) — read-only, not editable */}
                  <div className="tw-flex-1 tw-min-w-0">
                    <label
                      htmlFor="modal-featured"
                      className="tw-text-xs tw-font-medium tw-text-gray-400 tw-mb-1 tw-block"
                    >
                      Featured
                    </label>
                    <select
                      id="modal-featured"
                      value={formData.isFeatured ? 'F' : 'NF'}
                      disabled
                      className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-gray-400 tw-text-sm tw-cursor-not-allowed"
                    >
                      <option value="NF">Non-Featured (NF)</option>
                      <option value="F">Featured (F)</option>
                    </select>
                  </div>
                </div>

                {/* Contributions (Multiple) — click pill to set confidence category (same colors as table) */}
                <div>
                  <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                    <label
                      htmlFor="modal-contributions-input"
                      className={`tw-text-xs tw-font-medium ${
                        isFieldModified('contributions')
                          ? 'tw-text-orange-400'
                          : 'tw-text-white'
                      }`}
                    >
                      Contributions (Multiple)
                    </label>
                  </div>
                  <p className="tw-text-gray-400 tw-text-xs tw-mb-1.5">
                    Click a contribution to set confidence category and
                    evidence, or add a new contribution below.
                  </p>
                  <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2">
                    {[...contributions]
                      .map((cont, originalIndex) => ({
                        cont,
                        originalIndex,
                      }))
                      .sort(
                        (a, b) =>
                          parseInt(a.cont.confidenceCategory ?? '5', 10) -
                          parseInt(b.cont.confidenceCategory ?? '5', 10)
                      )
                      .map(({ cont, originalIndex }) => {
                        const style = getContributionPillStyle(
                          cont.confidenceCategory ?? '5'
                        );
                        return (
                          <div
                            key={originalIndex}
                            className="tw-relative tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-uppercase tw-cursor-pointer tw-border-2 tw-border-transparent hover:tw-border-white/50 tw-transition-colors"
                            style={style}
                            title="Click to change confidence category"
                          >
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                const c = contributions[originalIndex];
                                const { evidenceUrl, notes } =
                                  parseContributionEvidenceNotes(
                                    c.confidencePattern ?? ''
                                  );
                                setContributionModalForm({
                                  contributionName: c.contribution ?? '',
                                  confidenceCategory:
                                    c.confidenceCategory ?? '5',
                                  evidenceUrl,
                                  notes,
                                });
                                setContributionModalEvidenceError(null);
                                setContributionModalContext({
                                  type: 'edit',
                                  index: originalIndex,
                                });
                                setShowContributionModal(true);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const c = contributions[originalIndex];
                                  const { evidenceUrl, notes } =
                                    parseContributionEvidenceNotes(
                                      c.confidencePattern ?? ''
                                    );
                                  setContributionModalForm({
                                    contributionName: c.contribution ?? '',
                                    confidenceCategory:
                                      c.confidenceCategory ?? '5',
                                    evidenceUrl,
                                    notes,
                                  });
                                  setContributionModalEvidenceError(null);
                                  setContributionModalContext({
                                    type: 'edit',
                                    index: originalIndex,
                                  });
                                  setShowContributionModal(true);
                                }
                              }}
                              className="tw-flex-1"
                            >
                              {capitalize(cont.contribution)}
                            </span>
                            {contributions.length > 1 && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setContributions((prev) =>
                                    prev.filter((_, i) => i !== originalIndex)
                                  );
                                  if (
                                    contributionModalContext?.type === 'edit' &&
                                    contributionModalContext.index ===
                                      originalIndex
                                  ) {
                                    setShowContributionModal(false);
                                    setContributionModalContext(null);
                                  } else if (
                                    contributionModalContext?.type === 'edit' &&
                                    contributionModalContext.index >
                                      originalIndex
                                  ) {
                                    setContributionModalContext({
                                      type: 'edit',
                                      index: contributionModalContext.index - 1,
                                    });
                                  }
                                }}
                                className="tw-ml-0.5 tw-opacity-80 hover:tw-opacity-100 tw-flex tw-items-center"
                                title="Remove"
                              >
                                <X size={12} />
                              </button>
                            )}
                          </div>
                        );
                      })}
                  </div>
                  <div className="tw-flex tw-gap-1.5">
                    <select
                      id="modal-contributions-input"
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value) {
                          setContributionModalForm({
                            contributionName: value,
                            confidenceCategory: '5',
                            evidenceUrl: '',
                            notes: '',
                          });
                          setContributionModalEvidenceError(null);
                          setContributionModalContext({
                            type: 'add',
                            contributionValue: value,
                          });
                          setShowContributionModal(true);
                          e.target.value = '';
                        }
                      }}
                      className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                    >
                      <option value="">Select Contribution</option>
                      {availableContributions
                        .filter(
                          (cont) =>
                            !contributions.some((c) => c.contribution === cont)
                        )
                        .map((cont) => (
                          <option key={cont} value={cont}>
                            {capitalize(cont)}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      {!hideFooter && (
        <div
          className={`tw-flex tw-justify-end tw-gap-2 tw-mt-3 tw-pt-3 tw-px-3 tw-pb-3 tw-border-t tw-border-gray-600 ${
            stacked ? '' : 'tw-col-span-3'
          }`}
        >
          <WButton variant="secondary" label="Cancel" onClick={onCancel} />
          <WButton
            variant="primary"
            label="Save Changes"
            onClick={handleSave}
          />
        </div>
      )}

      {/* Release Correction Confirmation Modal */}
      <WModal
        isOpen={showReleaseCorrectionModal}
        onClose={() => setShowReleaseCorrectionModal(false)}
        title="Confirm Release Correction"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-6">
            You are about to correct release data that is linked to multiple
            recordings. Do you wish to proceed?
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => setShowReleaseCorrectionModal(false)}
            />
            <WButton
              variant="primary"
              label="Proceed"
              onClick={() => {
                setShowReleaseCorrectionModal(false);
                setIsReleaseEditable(true);
                // In a real implementation, this would trigger the release correction logic
                // For now, it's just a mock, so we enable editing
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Contribution Confidence modal */}
      <WModal
        isOpen={showContributionModal}
        onClose={() => {
          setShowContributionModal(false);
          setContributionModalContext(null);
          setContributionModalEvidenceError(null);
        }}
        title={`Contribution — ${
          contributionModalForm.contributionName
            ? capitalize(contributionModalForm.contributionName)
            : ''
        }`}
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4 tw-space-y-4">
          <div>
            <span className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
              Contribution
            </span>
            <p className="tw-text-white tw-text-sm">
              {contributionModalForm.contributionName
                ? capitalize(contributionModalForm.contributionName)
                : '—'}
            </p>
          </div>
          <div>
            <label
              htmlFor="contribution-modal-confidence"
              className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
            >
              Confidence category
            </label>
            <select
              id="contribution-modal-confidence"
              value={contributionModalForm.confidenceCategory}
              onChange={(e) =>
                setContributionModalForm((prev) => ({
                  ...prev,
                  confidenceCategory: e.target.value,
                }))
              }
              className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
            >
              {CONFIDENCE_CATEGORIES.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="contribution-modal-evidence"
              className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
            >
              Evidence URL <span className="tw-text-red-400">(required)</span>
            </label>
            <input
              id="contribution-modal-evidence"
              type="url"
              value={contributionModalForm.evidenceUrl}
              onChange={(e) => {
                setContributionModalForm((prev) => ({
                  ...prev,
                  evidenceUrl: e.target.value,
                }));
                if (contributionModalEvidenceError)
                  setContributionModalEvidenceError(null);
              }}
              placeholder="https://..."
              className={`tw-w-full tw-bg-gray-700 tw-border tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue ${
                contributionModalEvidenceError
                  ? 'tw-border-red-500'
                  : 'tw-border-gray-600'
              }`}
            />
            {contributionModalEvidenceError && (
              <p className="tw-text-red-400 tw-text-xs tw-mt-1">
                {contributionModalEvidenceError}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="contribution-modal-notes"
              className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
            >
              Notes <span className="tw-text-gray-400">(optional)</span>
            </label>
            <textarea
              id="contribution-modal-notes"
              value={contributionModalForm.notes}
              onChange={(e) =>
                setContributionModalForm((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              placeholder="Optional notes"
              rows={3}
              className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue tw-resize-y"
            />
          </div>
          <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setShowContributionModal(false);
                setContributionModalContext(null);
                setContributionModalEvidenceError(null);
              }}
            />
            <WButton
              variant="primary"
              label="Save"
              onClick={() => {
                const evidenceUrl =
                  contributionModalForm.evidenceUrl?.trim() ?? '';
                if (!evidenceUrl) {
                  setContributionModalEvidenceError(
                    'Evidence URL is required.'
                  );
                  return;
                }
                setContributionModalEvidenceError(null);
                const payload = {
                  contribution: contributionModalForm.contributionName,
                  confidenceCategory: contributionModalForm.confidenceCategory,
                  confidencePattern: JSON.stringify({
                    evidenceUrl,
                    notes: contributionModalForm.notes?.trim() ?? '',
                  }),
                };
                if (contributionModalContext?.type === 'edit') {
                  setContributions((prev) =>
                    prev.map((c, i) =>
                      i === contributionModalContext.index ? payload : c
                    )
                  );
                } else if (contributionModalContext?.type === 'add') {
                  setContributions((prev) => [...prev, payload]);
                }
                setShowContributionModal(false);
                setContributionModalContext(null);
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Create new artist modal */}
      <WModal
        isOpen={showCreateArtist}
        onClose={() => {
          setShowCreateArtist(false);
          setNewArtistAlias('');
          setNewArtistFeatured(false);
        }}
        title="Create new artist"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4 tw-space-y-4">
          <div>
            <label
              htmlFor="create-artist-alias"
              className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
            >
              Alias name
            </label>
            <input
              id="create-artist-alias"
              type="text"
              value={newArtistAlias}
              onChange={(e) => setNewArtistAlias(e.target.value)}
              placeholder="Enter artist alias"
              className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
            />
          </div>
          <label className="tw-flex tw-items-center tw-gap-2 tw-text-white tw-text-sm tw-cursor-pointer">
            <input
              type="checkbox"
              checked={newArtistFeatured}
              onChange={(e) => setNewArtistFeatured(e.target.checked)}
            />
            Featured
          </label>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setShowCreateArtist(false);
                setNewArtistAlias('');
                setNewArtistFeatured(false);
              }}
            />
            <WButton
              variant="primary"
              label="Add"
              onClick={() => {
                if (newArtistAlias.trim()) {
                  setArtists((prev) => [
                    ...prev,
                    {
                      name: newArtistAlias.trim(),
                      isFeatured: newArtistFeatured,
                    },
                  ]);
                  setNewArtistAlias('');
                  setNewArtistFeatured(false);
                  setShowCreateArtist(false);
                }
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Change Release Modal */}
      <WModal
        isOpen={showChangeReleaseModal}
        onClose={() => {
          setShowChangeReleaseModal(false);
          setReleaseId('');
          setSourceUrl('');
          setReleaseSource('Discogs');
          setNewReleaseTitle('');
          setNewReleaseYear('');
          setNewReleaseLabels([]);
          setNewReleaseCatNumbers([]);
          setNewReleaseFormat('');
          setNewReleaseType('');
          setReleaseLookupError(null);
          setReleaseLookupSuccess(false);
          setReleaseLookupNotFound(false);
          setReleaseLookupNotLinked(false);
          setReleaseNotLinkedConfirmed(false);
        }}
        title={
          releaseSource === 'Other' || releaseLookupNotFound
            ? 'Create and Link Release'
            : 'Change Linked Release'
        }
        modalClasses="tw-max-w-lg tw-w-full tw-mx-4 tw-max-h-[90vh]"
      >
        <div className="tw-mt-4 tw-overflow-y-auto tw-max-h-[calc(90vh-120px)]">
          <div className="tw-mb-4">
            <label
              htmlFor="release-source-input"
              className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
            >
              Release Source
            </label>
            <select
              id="release-source-input"
              value={releaseSource}
              onChange={(e) => {
                setReleaseSource(
                  e.target.value as
                    | 'Discogs'
                    | 'Deezer'
                    | 'Spotify'
                    | 'MusicBrainz'
                    | 'PPL'
                    | 'Other'
                );
                setReleaseId('');
                setSourceUrl('');
                setNewReleaseTitle('');
                setNewReleaseYear('');
                setNewReleaseLabels([]);
                setNewReleaseCatNumbers([]);
                setNewReleaseFormat('');
                setNewReleaseType('');
                setReleaseLookupError(null);
                setReleaseLookupSuccess(false);
                setReleaseLookupNotFound(false);
                setReleaseLookupNotLinked(false);
                setReleaseNotLinkedConfirmed(false);
              }}
              className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
            >
              <option value="Discogs">Discogs</option>
              <option value="Deezer">Deezer</option>
              <option value="Spotify">Spotify</option>
              <option value="MusicBrainz">MusicBrainz</option>
              <option value="PPL">PPL</option>
              <option value="Other">Other</option>
            </select>
          </div>
          {['Discogs', 'Deezer', 'Spotify', 'MusicBrainz', 'PPL'].includes(
            releaseSource
          ) ? (
            <div className="tw-mb-4">
              <label
                htmlFor="release-id-input"
                className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
              >
                Release ID
              </label>
              <div className="tw-flex tw-gap-2 tw-items-center">
                <input
                  id="release-id-input"
                  type="text"
                  value={releaseId}
                  onChange={(e) => {
                    setReleaseId(e.target.value);
                    setNewReleaseTitle('');
                    setNewReleaseYear('');
                    setNewReleaseLabels([]);
                    setNewReleaseCatNumbers([]);
                    setNewReleaseFormat('');
                    setNewReleaseType('');
                    setReleaseLookupError(null);
                    setReleaseLookupSuccess(false);
                    setReleaseLookupNotFound(false);
                    setReleaseLookupNotLinked(false);
                    setReleaseNotLinkedConfirmed(false);
                  }}
                  className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                  placeholder="Enter release ID"
                />
                <WButton
                  variant="secondary"
                  label={releaseLookupLoading ? 'Looking up…' : 'Lookup'}
                  disabled={
                    releaseLookupLoading ||
                    !releaseId.trim() ||
                    !userId ||
                    !row?.id
                  }
                  onClick={handleReleaseLookup}
                />
              </div>
              {releaseLookupError && (
                <p className="tw-text-westburyRed tw-text-xs tw-mt-2">
                  {releaseLookupError}
                </p>
              )}
              {releaseLookupNotFound && (
                <div className="tw-mt-4 tw-space-y-4 tw-pt-4 tw-border-t tw-border-gray-600">
                  <p className="tw-text-orange-400 tw-text-sm">
                    Release not found, please provide the details.
                  </p>
                  <div className="tw-flex tw-gap-4 tw-w-full">
                    <div className="tw-flex-1 tw-min-w-0">
                      <label
                        htmlFor="change-release-notfound-title"
                        className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                      >
                        Release Title
                      </label>
                      <input
                        id="change-release-notfound-title"
                        type="text"
                        value={newReleaseTitle}
                        onChange={(e) => setNewReleaseTitle(e.target.value)}
                        className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                        placeholder="Enter release title"
                      />
                    </div>
                    <div className="tw-flex-1 tw-min-w-0">
                      <label
                        htmlFor="change-release-notfound-year"
                        className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                      >
                        Year of Release
                      </label>
                      <input
                        id="change-release-notfound-year"
                        type="text"
                        value={newReleaseYear}
                        onChange={(e) => setNewReleaseYear(e.target.value)}
                        className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                        placeholder="YYYY"
                        maxLength={4}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <label className="tw-block tw-text-white tw-text-sm tw-font-medium">
                        Label (Multiple)
                      </label>
                      <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                        Optional
                      </span>
                    </div>
                    {newReleaseLabels.length > 0 && (
                      <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2">
                        {newReleaseLabels.map((label, index) => (
                          <div
                            key={index}
                            className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white"
                          >
                            <span>{label}</span>
                            <button
                              type="button"
                              onClick={() =>
                                setNewReleaseLabels((prev) =>
                                  prev.filter((_, i) => i !== index)
                                )
                              }
                              className="tw-ml-1 tw-text-gray-400 hover:tw-text-white"
                              title="Remove"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="tw-flex tw-flex-col tw-gap-1.5">
                      <input
                        id="change-release-notfound-labels-input"
                        type="text"
                        placeholder="Enter label"
                        onKeyDown={(e) => {
                          if (
                            e.key === 'Enter' &&
                            e.currentTarget.value.trim()
                          ) {
                            addMultipleValue(
                              setNewReleaseLabels,
                              e.currentTarget.value
                            );
                            e.currentTarget.value = '';
                          }
                        }}
                        className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                      />
                      <div className="tw-flex tw-justify-end">
                        <button
                          type="button"
                          className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                          onClick={() => {
                            const input = document.getElementById(
                              'change-release-notfound-labels-input'
                            ) as HTMLInputElement;
                            if (input?.value.trim()) {
                              addMultipleValue(
                                setNewReleaseLabels,
                                input.value
                              );
                              input.value = '';
                            }
                          }}
                        >
                          <i className="wi wi-20px wi-plus tw-mr-1" />
                          <span className="tw-underline">Add label</span>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <label className="tw-block tw-text-white tw-text-sm tw-font-medium">
                        Catalogue Number (Multiple)
                      </label>
                      <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                        Optional
                      </span>
                    </div>
                    {newReleaseCatNumbers.length > 0 && (
                      <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2">
                        {newReleaseCatNumbers.map((catNo, index) => (
                          <div
                            key={index}
                            className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white"
                          >
                            <span>{catNo}</span>
                            <button
                              type="button"
                              onClick={() =>
                                setNewReleaseCatNumbers((prev) =>
                                  prev.filter((_, i) => i !== index)
                                )
                              }
                              className="tw-ml-1 tw-text-gray-400 hover:tw-text-white"
                              title="Remove"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="tw-flex tw-flex-col tw-gap-1.5">
                      <input
                        id="change-release-notfound-catno-input"
                        type="text"
                        placeholder="Enter catalogue number"
                        onKeyDown={(e) => {
                          if (
                            e.key === 'Enter' &&
                            e.currentTarget.value.trim()
                          ) {
                            addMultipleValue(
                              setNewReleaseCatNumbers,
                              e.currentTarget.value
                            );
                            e.currentTarget.value = '';
                          }
                        }}
                        className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                      />
                      <div className="tw-flex tw-justify-end">
                        <button
                          type="button"
                          className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                          onClick={() => {
                            const input = document.getElementById(
                              'change-release-notfound-catno-input'
                            ) as HTMLInputElement;
                            if (input?.value.trim()) {
                              addMultipleValue(
                                setNewReleaseCatNumbers,
                                input.value
                              );
                              input.value = '';
                            }
                          }}
                        >
                          <i className="wi wi-20px wi-plus tw-mr-1" />
                          <span className="tw-underline">
                            Add catalogue number
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="tw-flex tw-gap-4 tw-w-full">
                    <div className="tw-flex-1 tw-min-w-0">
                      <label
                        htmlFor="change-release-notfound-format"
                        className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                      >
                        Format
                      </label>
                      <select
                        id="change-release-notfound-format"
                        value={newReleaseFormat}
                        onChange={(e) => setNewReleaseFormat(e.target.value)}
                        className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                      >
                        <option value="">Select Format</option>
                        {availableFormats.map((format) => (
                          <option key={format} value={format}>
                            {format}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="tw-flex-1 tw-min-w-0">
                      <label
                        htmlFor="change-release-notfound-type"
                        className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                      >
                        Type
                      </label>
                      <select
                        id="change-release-notfound-type"
                        value={newReleaseType}
                        onChange={(e) => setNewReleaseType(e.target.value)}
                        className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                      >
                        <option value="">Select Type</option>
                        {availableTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
              {releaseLookupSuccess && (
                <div className="tw-mt-4 tw-space-y-4 tw-pt-4 tw-border-t tw-border-gray-600">
                  <div className="tw-flex tw-gap-4 tw-w-full">
                    <div className="tw-flex-1">
                      <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                        Release Title
                      </label>
                      <input
                        type="text"
                        value={newReleaseTitle}
                        readOnly
                        className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-cursor-not-allowed tw-opacity-90"
                      />
                    </div>
                    <div className="tw-max-w-28">
                      <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                        Year of Release
                      </label>
                      <input
                        type="text"
                        value={newReleaseYear}
                        readOnly
                        className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-cursor-not-allowed tw-opacity-90"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                      Label (Multiple)
                    </label>
                    <div className="tw-min-h-[40px] tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-flex tw-flex-wrap tw-gap-1.5 tw-items-center">
                      {newReleaseLabels.length > 0 ? (
                        newReleaseLabels.map((label, index) => (
                          <span
                            key={index}
                            className="tw-inline-flex tw-rounded-xs tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-bg-gray-700"
                          >
                            {label}
                          </span>
                        ))
                      ) : (
                        <span className="tw-text-gray-500">—</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                      Catalogue Number (Multiple)
                    </label>
                    {newReleaseCatNumbers.length > 0 && (
                      <div className="tw-min-h-[40px] tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-flex tw-flex-wrap tw-gap-1.5 tw-items-center">
                        {newReleaseCatNumbers.length > 0 ? (
                          newReleaseCatNumbers.map((catNo, index) => (
                            <span
                              key={index}
                              className="tw-inline-flex tw-rounded-xs tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-bg-gray-700"
                            >
                              {catNo}
                            </span>
                          ))
                        ) : (
                          <span className="tw-text-gray-500">—</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="tw-flex tw-gap-4 tw-w-full">
                    <div className="tw-w-1/2">
                      <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                        Format
                      </label>
                      <input
                        type="text"
                        value={newReleaseFormat}
                        readOnly
                        className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-cursor-not-allowed tw-opacity-90"
                      />
                    </div>
                    <div className="tw-w-1/2">
                      <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                        Type
                      </label>
                      <input
                        type="text"
                        value={newReleaseType}
                        readOnly
                        className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-cursor-not-allowed tw-opacity-90"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="tw-space-y-4">
              <div>
                <label
                  htmlFor="new-release-source-url"
                  className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                >
                  Source
                </label>
                <input
                  id="new-release-source-url"
                  type="url"
                  value={sourceUrl}
                  onChange={(e) => {
                    setSourceUrl(e.target.value);
                    setNewReleaseTitle('');
                    setNewReleaseYear('');
                    setNewReleaseLabels([]);
                    setNewReleaseCatNumbers([]);
                    setNewReleaseFormat('');
                    setNewReleaseType('');
                  }}
                  className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                  placeholder="Provide release source details"
                />
              </div>
              <div className="tw-flex tw-gap-4 tw-w-full">
                <div className="tw-flex-1 tw-min-w-0">
                  <label
                    htmlFor="new-release-title"
                    className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                  >
                    Release Title
                  </label>
                  <input
                    id="new-release-title"
                    type="text"
                    value={newReleaseTitle}
                    onChange={(e) => setNewReleaseTitle(e.target.value)}
                    className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                    placeholder="Enter release title"
                  />
                </div>
                <div className="tw-flex-1 tw-min-w-0">
                  <label
                    htmlFor="new-release-year"
                    className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                  >
                    Year of Release
                  </label>
                  <input
                    id="new-release-year"
                    type="text"
                    value={newReleaseYear}
                    onChange={(e) => setNewReleaseYear(e.target.value)}
                    className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                    placeholder="YYYY"
                    maxLength={4}
                  />
                </div>
              </div>
              <div>
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                  <label
                    htmlFor="new-release-labels-input"
                    className="tw-block tw-text-white tw-text-sm tw-font-medium"
                  >
                    Label (Multiple)
                  </label>
                  <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                    Optional
                  </span>
                </div>
                {newReleaseLabels.length > 0 && (
                  <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2">
                    {newReleaseLabels.map((label, index) => (
                      <div
                        key={index}
                        className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white"
                      >
                        <span>{label}</span>
                        <button
                          type="button"
                          onClick={() =>
                            setNewReleaseLabels((prev) =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="tw-ml-1 tw-text-gray-400 hover:tw-text-white"
                          title="Remove"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <input
                    id="new-release-labels-input"
                    type="text"
                    placeholder="Enter label"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                        addMultipleValue(
                          setNewReleaseLabels,
                          e.currentTarget.value
                        );
                        e.currentTarget.value = '';
                      }
                    }}
                    className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                  />
                  <div className="tw-flex tw-justify-end">
                    <button
                      type="button"
                      className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                      onClick={() => {
                        const input = document.getElementById(
                          'new-release-labels-input'
                        ) as HTMLInputElement;
                        if (input?.value.trim()) {
                          addMultipleValue(setNewReleaseLabels, input.value);
                          input.value = '';
                        }
                      }}
                    >
                      <i className="wi wi-20px wi-plus tw-mr-1" />
                      <span className="tw-underline">Add label</span>
                    </button>
                  </div>
                </div>
              </div>
              <div>
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                  <label
                    htmlFor="new-release-catno-input"
                    className="tw-block tw-text-white tw-text-sm tw-font-medium"
                  >
                    Catalogue Number (Multiple)
                  </label>
                  <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                    Optional
                  </span>
                </div>
                {newReleaseCatNumbers.length > 0 && (
                  <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2">
                    {newReleaseCatNumbers.map((catNo, index) => (
                      <div
                        key={index}
                        className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white"
                      >
                        <span>{catNo}</span>
                        <button
                          type="button"
                          onClick={() =>
                            setNewReleaseCatNumbers((prev) =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="tw-ml-1 tw-text-gray-400 hover:tw-text-white"
                          title="Remove"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <input
                    id="new-release-catno-input"
                    type="text"
                    placeholder="Enter catalogue number"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                        addMultipleValue(
                          setNewReleaseCatNumbers,
                          e.currentTarget.value
                        );
                        e.currentTarget.value = '';
                      }
                    }}
                    className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                  />
                  <div className="tw-flex tw-justify-end">
                    <button
                      type="button"
                      className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                      onClick={() => {
                        const input = document.getElementById(
                          'new-release-catno-input'
                        ) as HTMLInputElement;
                        if (input?.value.trim()) {
                          addMultipleValue(
                            setNewReleaseCatNumbers,
                            input.value
                          );
                          input.value = '';
                        }
                      }}
                    >
                      <i className="wi wi-20px wi-plus tw-mr-1" />
                      <span className="tw-underline">Add catalogue number</span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="tw-flex tw-gap-4 tw-w-full">
                <div className="tw-flex-1 tw-min-w-0">
                  <label
                    htmlFor="new-release-format"
                    className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                  >
                    Format
                  </label>
                  <select
                    id="new-release-format"
                    value={newReleaseFormat}
                    onChange={(e) => setNewReleaseFormat(e.target.value)}
                    className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                  >
                    <option value="">Select Format</option>
                    {availableFormats.map((format) => (
                      <option key={format} value={format}>
                        {format}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="tw-flex-1 tw-min-w-0">
                  <label
                    htmlFor="new-release-type"
                    className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                  >
                    Type
                  </label>
                  <select
                    id="new-release-type"
                    value={newReleaseType}
                    onChange={(e) => setNewReleaseType(e.target.value)}
                    className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                  >
                    <option value="">Select Type</option>
                    {availableTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
          {releaseLookupNotLinked && (
            <div
              className="tw-rounded-lg tw-border tw-border-orange-500/60 tw-bg-orange-500/10 tw-p-4"
              role="alert"
            >
              <p className="tw-text-orange-400 tw-text-sm tw-font-medium tw-mb-2">
                Confirm Release Linking
              </p>
              <p className="tw-text-gray-300 tw-text-sm tw-mb-4">
                This release is already linked to a different recording.
              </p>
              <p className="tw-text-gray-300 tw-text-sm tw-mb-4">
                Please double-check that you are linking it to the correct
                recording and not a different version.
              </p>
              <label
                htmlFor="change-release-not-linked-confirmed"
                className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer"
              >
                <input
                  id="change-release-not-linked-confirmed"
                  type="checkbox"
                  checked={releaseNotLinkedConfirmed}
                  onChange={(e) =>
                    setReleaseNotLinkedConfirmed(e.target.checked)
                  }
                  className="tw-mt-0.5 tw-rounded tw-border-gray-600 tw-bg-gray-700 tw-text-orange-500 focus:tw-ring-orange-500"
                />
                <span className="tw-text-sm tw-text-white">
                  I have read and understood this warning
                </span>
              </label>
            </div>
          )}
          <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-600">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setShowChangeReleaseModal(false);
                setReleaseId('');
                setSourceUrl('');
                setReleaseSource('Discogs');
                setNewReleaseTitle('');
                setNewReleaseYear('');
                setNewReleaseLabels([]);
                setNewReleaseCatNumbers([]);
                setNewReleaseFormat('');
                setNewReleaseType('');
                setReleaseLookupError(null);
                setReleaseLookupSuccess(false);
                setReleaseLookupNotFound(false);
                setReleaseLookupNotLinked(false);
                setReleaseNotLinkedConfirmed(false);
              }}
            />
            <WButton
              variant="primary"
              label={
                releaseSource === 'Other' || releaseLookupNotFound
                  ? 'Create and Link Release'
                  : 'Change Linked Release'
              }
              disabled={
                releaseSource === 'Discogs'
                  ? releaseLookupNotFound
                    ? !(
                        newReleaseTitle.trim() &&
                        newReleaseYear.trim() &&
                        newReleaseFormat &&
                        newReleaseType
                      )
                    : !(
                        releaseLookupSuccess &&
                        (releaseLookupNotLinked
                          ? releaseNotLinkedConfirmed
                          : true)
                      )
                  : !sourceUrl.trim() ||
                    !newReleaseTitle.trim() ||
                    !newReleaseYear.trim() ||
                    !newReleaseFormat ||
                    !newReleaseType
              }
              onClick={() => {
                const discogsCanSubmit = releaseLookupNotFound
                  ? Boolean(
                      newReleaseTitle.trim() &&
                        newReleaseYear.trim() &&
                        newReleaseFormat &&
                        newReleaseType
                    )
                  : Boolean(
                      releaseLookupSuccess &&
                        (releaseLookupNotLinked
                          ? releaseNotLinkedConfirmed
                          : true)
                    );
                if (releaseSource === 'Discogs' && discogsCanSubmit) {
                  setShowChangeReleaseModal(false);
                  setReleaseId('');
                  setReleaseSource('Discogs');
                  setReleaseLookupSuccess(false);
                  setReleaseLookupNotFound(false);
                  setReleaseLookupNotLinked(false);
                  setReleaseNotLinkedConfirmed(false);
                } else if (
                  releaseSource === 'Other' &&
                  sourceUrl.trim() &&
                  newReleaseTitle.trim() &&
                  newReleaseYear.trim() &&
                  newReleaseFormat &&
                  newReleaseType
                ) {
                  setShowChangeReleaseModal(false);
                  setSourceUrl('');
                  setReleaseSource('Discogs');
                  setNewReleaseTitle('');
                  setNewReleaseYear('');
                  setNewReleaseLabels([]);
                  setNewReleaseCatNumbers([]);
                  setNewReleaseFormat('');
                  setNewReleaseType('');
                }
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Live → Non-Live warning modal */}
      <WModal
        isOpen={showLiveWarningModal}
        onClose={() => {
          setShowLiveWarningModal(false);
          setPendingLiveValue(null);
        }}
        title="Switch to Non-Live?"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-6">
            You are switching this recording from Live to Non-Live. Do you wish
            to proceed?
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setShowLiveWarningModal(false);
                setPendingLiveValue(null);
              }}
            />
            <WButton
              variant="primary"
              label="Proceed"
              onClick={() => {
                if (pendingLiveValue === false) {
                  handleFieldChange('isLive', false);
                }
                setShowLiveWarningModal(false);
                setPendingLiveValue(null);
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Revert Confirmation Modal */}
      <WModal
        isOpen={showRevertModal}
        onClose={() => {
          setShowRevertModal(false);
          setRevertTarget(null);
        }}
        title="Confirm Revert"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-6">
            Do you want to revert to the original value?
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setShowRevertModal(false);
                setRevertTarget(null);
              }}
            />
            <WButton
              variant="primary"
              label="Revert"
              onClick={() => {
                if (revertTarget) {
                  handleRevert(revertTarget.type, revertTarget.field);
                }
              }}
            />
          </div>
        </div>
      </WModal>
    </>
  );
});
